import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Gauge, AlertTriangle, CheckCircle2 } from "lucide-react";

const Stat = ({ label, value, sub, testId }) => (
  <div className="bg-slate-950/60 border border-slate-800 rounded-md p-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-mono text-xl text-slate-100 mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

function Section({ title, cols, testId }) {
  return (
    <div data-testid={testId}>
      <div className="label-cap mb-2">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">{cols.map((c) => <Stat key={c.label} {...c} />)}</div>
    </div>
  );
}

function StatusBadge({ q }) {
  const current = q.status === "current";
  const cls = current ? "text-emerald-400 border-emerald-700/60 bg-emerald-950/40" : "text-amber-300 border-amber-700/60 bg-amber-950/40";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded border ${cls}`} data-testid="code-quality-status">
      {current ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
      {current ? "Current" : "Stale"} · report {q.report_commit} · app {q.current_commit}
    </span>
  );
}

export function CodeQualityCard() {
  const [q, setQ] = useState(null);
  useEffect(() => { api.get("/platform/code-quality").then((r) => setQ(r.data)).catch(() => setQ({ available: false, status: "error" })); }, []);
  if (!q) return <div className="sc-card p-5 text-slate-400 text-sm">Loading code quality…</div>;
  if (!q.available) return <div className="sc-card p-5 text-slate-400 text-sm" data-testid="code-quality-missing">No quality report generated yet. Run <code className="font-mono text-slate-200">python backend/scripts/quality_report.py</code> offline and redeploy.</div>;
  const p = q.production_python, t = q.test_python, r = q.production_react, u = q.ui_components;
  return (
    <div className="sc-card p-5 space-y-5" data-testid="code-quality-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-amber-500 flex items-center gap-2"><Gauge className="w-4 h-4" /> CODE QUALITY (authoritative analyzers)</div>
          <div className="text-xs text-slate-400 mt-1">Generated {new Date(q.generated_at).toLocaleString()} · ruff · pyflakes · pylint · {q.analyzers.pyright ? "pyright · " : ""}radon · eslint react-hooks. Read-only; analyzers run offline.</div>
        </div>
        <StatusBadge q={q} />
      </div>
      {q.status === "stale" && <div className="text-xs text-amber-300 border border-amber-800/60 bg-amber-950/30 rounded p-2" data-testid="code-quality-stale-note">This report was generated for commit {q.report_commit}; the running app is {q.current_commit}. Metrics below may not reflect current code.</div>}
      <div className="grid grid-cols-2 gap-2 max-w-md">
        <Stat label="Production Python score" value={`${q.scores.production_python}/100`} testId="score-production-python" />
        <Stat label="Production React score" value={`${q.scores.production_react}/100`} testId="score-production-react" />
      </div>
      <Section title="Production Python" testId="section-production-python" cols={[
        { label: "Files / functions", value: `${p.files} / ${p.functions}` },
        { label: "Type-hint coverage", value: `${p.type_hint_coverage_pct}%` },
        { label: "Confirmed defects", value: p.confirmed_defects, sub: `${p.false_positives} heuristic false positives`, testId: "prod-py-defects" },
        { label: "Complexity", value: `${p.cc_over_10} > CC10`, sub: `${p.cc_13_plus.length} at CC≥13 · ${p.long_functions.length} long` },
      ]} />
      <Section title="Test Python (regression coverage — not in production score)" testId="section-test-python" cols={[
        { label: "Files / functions", value: `${t.files} / ${t.functions}` },
        { label: "Type-hint coverage", value: `${t.type_hint_coverage_pct}%` },
        { label: "Confirmed defects", value: t.confirmed_defects, sub: `${t.false_positives} heuristic false positives` },
        { label: "Complexity", value: `${t.cc_over_10} > CC10`, sub: `${t.long_functions} long` },
      ]} />
      <Section title="Production React" testId="section-production-react" cols={[
        { label: "Files / lines", value: `${r.files} / ${r.lines.toLocaleString()}` },
        { label: "Hook dependency warnings", value: r.hook_warnings, sub: `${r.heuristic_refs} heuristic refs, all false positives`, testId: "prod-react-hooks" },
        { label: "Console statements", value: r.console_statements },
        { label: "Files > 150 lines", value: r.large_files },
      ]} />
      <Section title="UI / generated components (shadcn — excluded from score)" testId="section-ui-components" cols={[
        { label: "Files / lines", value: `${u.files} / ${u.lines.toLocaleString()}` },
        { label: "Files > 150 lines", value: u.large_files },
      ]} />
      <div className="text-xs text-slate-500 flex flex-wrap gap-4" data-testid="code-quality-legend">
        {Object.entries(q.legend).map(([k, v]) => <span key={k}><span className="text-slate-300 font-semibold">{k}</span>: {v}</span>)}
      </div>
    </div>
  );
}
